<template>
    <div v-if="showMetaPreview" class="share-banner-preview--meta" :style="{ width: `${previewAdInputSize.width}px` }">
        <div class="ad-preview-header-wrapper">
            <Select v-model="adPreviewType" class="header-action-select" transfer>
                <Option v-for="option in META_AD_PREVIEW_OPTIONS" :key="option.value" :value="option.value">
                    {{ option.label }}
                    <span v-if="option.subLabel">{{ option.subLabel }}</span>
                </Option>
            </Select>
        </div>

        <div class="banner-container">
            <MetaPreviewFrame
                :meta-overwrites="metaOverwrites"
                :master-template="bannerPreviewData.masterTemplate"
                :ad-preview-type="adPreviewType"
                @setInStreamValue="setInStreamValue"
            >
                <BannerPreview
                    :id="bannerId"
                    content-clickable
                    lazy-load
                    :preview-values="previewValues"
                    :ad-type="adType"
                    :scaling-factor="previewAdInputSize.adScalingFactor"
                    :meta-preview-size="previewAdInputSize"
                    :playback-namespace="playbackNamespace"
                    @bannerLoaded="onBannerLoaded"
                />
            </MetaPreviewFrame>
        </div>

        <div class="share-banner-preview__footer">
            <BannerFooterName
                :is-dark-theme="false"
                :banner-name="bannerName"
                class="share-banner-preview__footer--template-name"
            />
            <BannerFooterControls :banner-id="bannerId" :playback-namespace="playbackNamespace" />
        </div>
    </div>

    <CampaignBanner
        v-else
        :master-template-height="displayDimensions.height"
        :master-template-width="displayDimensions.width"
        :max-height="maxHeight"
        :max-width="maxWidth"
        :scaling-factor="scalingFactor"
        @updateInternalScalingFactor="internalScalingFactor = $event"
    >
        <template #bannerContainer>
            <img
                v-if="bannerPreviewData.masterTemplate.adType === TemplateType.PSD"
                class="psd-template"
                :src="bannerPreviewData.previewUrl"
            />

            <BannerPreview
                v-else
                :id="bannerId"
                content-clickable
                lazy-load
                :preview-values="previewValues"
                :ad-type="adType"
                :scaling-factor="internalScalingFactor"
                :playback-namespace="playbackNamespace"
                @bannerLoaded="onBannerLoaded"
            />
        </template>

        <template #bannerFooter>
            <BannerFooterName :is-dark-theme="false" :banner-name="bannerName" />
            <BannerFooterControls :banner-id="bannerId" :playback-namespace="playbackNamespace" />
        </template>
    </CampaignBanner>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import type { BannerPreviewData, MetaOverwrites } from "@/types/types";
import { BannerEvents } from "@/enums/banners";
import { TemplateType } from "@/enums/masterTemplate";

import BannerPreview from "@/components/Previews/BannerPreview.vue";
import CampaignBanner from "@/components/Campaign/CampaignBanner.vue";
import MetaPreviewFrame from "@/components/Campaign/AdPreview/MetaPreviewFrame.vue";
import BannerFooterName from "@/components/Previews/BannerFooter/BannerFooterName.vue";
import BannerFooterControls from "@/components/Previews/BannerFooter/BannerFooterControls.vue";

import { useMetaAdPreview } from "@/composables/useMetaAdPreview";
import { getBannerIdFromBannerPreviewData } from "@/utils/getBannerIframeId";
import { formatTemplateSizeLabel, getTemplateDisplayDimensions } from "@/utils";
import { META_AD_PREVIEW_OPTIONS } from "@/enums/adPreview";

export default defineComponent({
    components: {
        CampaignBanner,
        BannerPreview,
        MetaPreviewFrame,
        BannerFooterName,
        BannerFooterControls
    },

    props: {
        bannerPreviewData: {
            type: Object as PropType<BannerPreviewData>,
            required: true
        },

        playbackNamespace: {
            type: String,
            required: true
        },

        maxWidth: {
            type: Number,
            required: true
        },

        maxHeight: {
            type: Number,
            required: true
        },

        scalingFactor: {
            type: Number,
            required: true
        },

        showMetaPreview: {
            type: Boolean,
            default: false
        }
    },

    emits: [BannerEvents.BannerLoaded],

    setup(props) {
        const { adPreviewType, previewAdInputSize, setInStreamValue } = useMetaAdPreview(
            () => props.bannerPreviewData.masterTemplate
        );

        return {
            adPreviewType,
            previewAdInputSize,
            setInStreamValue
        };
    },

    data() {
        return {
            internalScalingFactor: 1,
            TemplateType,
            META_AD_PREVIEW_OPTIONS
        };
    },

    computed: {
        adType() {
            return this.bannerPreviewData.masterTemplate.adType;
        },

        metaOverwrites(): MetaOverwrites {
            return (this.bannerPreviewData.platformMetadata ?? {}) as MetaOverwrites;
        },

        displayDimensions() {
            return getTemplateDisplayDimensions(this.bannerPreviewData.masterTemplate);
        },

        previewValues() {
            return JSON.stringify(this.bannerPreviewData);
        },

        bannerId() {
            return getBannerIdFromBannerPreviewData(this.bannerPreviewData, "share");
        },

        bannerName() {
            return `${this.bannerPreviewData.reportingLabel} | ${formatTemplateSizeLabel(this.bannerPreviewData.masterTemplate)} - ${this.bannerPreviewData.masterTemplate.name}`;
        }
    },

    methods: {
        onBannerLoaded() {
            this.$emit(BannerEvents.BannerLoaded);
        }
    }
});
</script>

<style scoped lang="css">
.psd-template {
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: center;

    background-color: white;
}

.share-banner-preview--meta {
    display: flex;
    flex-direction: column;
    box-sizing: content-box;
    background-color: #fff;
}

.ad-preview-header-wrapper {
    padding: 10px;
    background-color: #fff;

    display: flex;
    flex-direction: column;
    gap: 8px;
}

.ad-preview-header-wrapper .header-action-select {
    max-width: unset;
}

.ad-preview-header-wrapper .header-action-select span {
    display: block;
    font-size: 12px;
}

.share-banner-preview__footer {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    .share-banner-preview__footer--template-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

.banner-container {
    flex: 1 1 auto;
    display: flex;
    position: relative;
}
</style>
